// src/app/services/theme.service.ts
import { Injectable, Inject, Renderer2, RendererFactory2 } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import themes from 'devextreme/ui/themes';
import { BehaviorSubject } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface ThemeColorScheme {
  primary: string;
  secondary: string;
  tertiary: string;
  success: string;
  warning: string;
  danger: string;
  background: string;
  surface: string;
  text: string;
  textSecondary: string;
}

// Eğer ayrı dosyada tutuyorsan bunu kendi yoluna göre güncelle
// (Aşağıdaki THEME_PRESETS yapısını istersen buraya da gömebilirsin.)
export interface ThemePreset {
  id: string;        // "blue-light", "purple-dark" ...
  title: string;     // Ekranda görünen ad
  ionicKey: string;  // body[data-theme="..."]  (palettes.scss ile eşleşir)
  devx: DevXTheme;   // DevExtreme tema adı
  dark: boolean;     // Ionic dark sınıfı (body.dark)
  colors: ThemeColorScheme; // Custom renk şeması
}

export type DevXTheme =
  // Material themes
  | 'material.blue.light'  | 'material.blue.dark'
  | 'material.lime.light'  | 'material.lime.dark'
  | 'material.orange.light'| 'material.orange.dark'
  | 'material.purple.light'| 'material.purple.dark'
  | 'material.teal.light'  | 'material.teal.dark'
  // Light themes
  | 'light'           | 'light.compact'
  // Dark themes
  | 'dark'            | 'dark.compact'
  | 'darkmoon'        | 'darkmoon.compact'
  | 'darkviolet'      | 'darkviolet.compact';

// Tek yerde yönet: yanda açılacak liste bundan beslenecek
export const THEME_PRESETS: ThemePreset[] = [
  // === Material Themes ===
  { 
    id: 'blue-light',   
    title: 'Material Blue Light',   
    ionicKey: 'blue-light',   
    devx: 'material.blue.light',   
    dark: false,
    colors: { primary: '#2196F3', secondary: '#1976D2', tertiary: '#0D47A1', success: '#4CAF50', warning: '#FF9800', danger: '#F44336', background: '#FFFFFF', surface: '#F5F5F5', text: '#000000', textSecondary: '#666666' }
  },
  { 
    id: 'blue-dark',    
    title: 'Material Blue Dark',    
    ionicKey: 'blue-dark',    
    devx: 'material.blue.dark',    
    dark: true,
    colors: { primary: '#64B5F6', secondary: '#42A5F5', tertiary: '#2196F3', success: '#81C784', warning: '#FFB74D', danger: '#EF5350', background: '#121212', surface: '#1E1E1E', text: '#FFFFFF', textSecondary: '#B0B0B0' }
  },
  { 
    id: 'lime-light',   
    title: 'Material Lime Light',   
    ionicKey: 'lime-light',   
    devx: 'material.lime.light',   
    dark: false,
    colors: { primary: '#CDDC39', secondary: '#AFB42B', tertiary: '#827717', success: '#4CAF50', warning: '#FF9800', danger: '#F44336', background: '#FFFFFF', surface: '#F5F5F5', text: '#000000', textSecondary: '#666666' }
  },
  { 
    id: 'lime-dark',    
    title: 'Material Lime Dark',    
    ionicKey: 'lime-dark',    
    devx: 'material.lime.dark',    
    dark: true,
    colors: { primary: '#E6EE9C', secondary: '#DCE775', tertiary: '#CDDC39', success: '#81C784', warning: '#FFB74D', danger: '#EF5350', background: '#121212', surface: '#1E1E1E', text: '#FFFFFF', textSecondary: '#B0B0B0' }
  },
  { 
    id: 'orange-light', 
    title: 'Material Orange Light', 
    ionicKey: 'orange-light', 
    devx: 'material.orange.light', 
    dark: false,
    colors: { primary: '#FF9800', secondary: '#F57C00', tertiary: '#E65100', success: '#4CAF50', warning: '#FFC107', danger: '#F44336', background: '#FFFFFF', surface: '#F5F5F5', text: '#000000', textSecondary: '#666666' }
  },
  { 
    id: 'orange-dark',  
    title: 'Material Orange Dark',  
    ionicKey: 'orange-dark',  
    devx: 'material.orange.dark',  
    dark: true,
    colors: { primary: '#FFB74D', secondary: '#FFA726', tertiary: '#FF9800', success: '#81C784', warning: '#FFD54F', danger: '#EF5350', background: '#121212', surface: '#1E1E1E', text: '#FFFFFF', textSecondary: '#B0B0B0' }
  },
  { 
    id: 'purple-light', 
    title: 'Material Purple Light', 
    ionicKey: 'purple-light', 
    devx: 'material.purple.light', 
    dark: false,
    colors: { primary: '#9C27B0', secondary: '#7B1FA2', tertiary: '#4A148C', success: '#4CAF50', warning: '#FF9800', danger: '#F44336', background: '#FFFFFF', surface: '#F5F5F5', text: '#000000', textSecondary: '#666666' }
  },
  { 
    id: 'purple-dark',  
    title: 'Material Purple Dark',  
    ionicKey: 'purple-dark',  
    devx: 'material.purple.dark',  
    dark: true,
    colors: { primary: '#CE93D8', secondary: '#BA68C8', tertiary: '#AB47BC', success: '#81C784', warning: '#FFB74D', danger: '#EF5350', background: '#121212', surface: '#1E1E1E', text: '#FFFFFF', textSecondary: '#B0B0B0' }
  },
  { 
    id: 'teal-light',   
    title: 'Material Teal Light',   
    ionicKey: 'teal-light',   
    devx: 'material.teal.light',   
    dark: false,
    colors: { primary: '#009688', secondary: '#00796B', tertiary: '#004D40', success: '#4CAF50', warning: '#FF9800', danger: '#F44336', background: '#FFFFFF', surface: '#F5F5F5', text: '#000000', textSecondary: '#666666' }
  },
  { 
    id: 'teal-dark',    
    title: 'Material Teal Dark',    
    ionicKey: 'teal-dark',    
    devx: 'material.teal.dark',    
    dark: true,
    colors: { primary: '#4DB6AC', secondary: '#26A69A', tertiary: '#009688', success: '#81C784', warning: '#FFB74D', danger: '#EF5350', background: '#121212', surface: '#1E1E1E', text: '#FFFFFF', textSecondary: '#B0B0B0' }
  },
  
  // === Light Themes (Bootstrap style) ===
  { 
    id: 'light-blue',   
    title: 'Light Blue',           
    ionicKey: 'blue-light',   
    devx: 'light',                 
    dark: false,
    colors: { primary: '#0066CC', secondary: '#0052A3', tertiary: '#003D7A', success: '#28A745', warning: '#FFC107', danger: '#DC3545', background: '#FFFFFF', surface: '#F8F9FA', text: '#212529', textSecondary: '#6C757D' }
  },
  { 
    id: 'light-compact', 
    title: 'Light Compact Blue',  
    ionicKey: 'blue-light',   
    devx: 'light.compact',         
    dark: false,
    colors: { primary: '#0066CC', secondary: '#0052A3', tertiary: '#003D7A', success: '#28A745', warning: '#FFC107', danger: '#DC3545', background: '#FFFFFF', surface: '#F8F9FA', text: '#212529', textSecondary: '#6C757D' }
  },
  
  // === Dark Themes ===
  { 
    id: 'dark-blue',    
    title: 'Dark Blue',            
    ionicKey: 'blue-dark',    
    devx: 'dark',                  
    dark: true,
    colors: { primary: '#4DA6FF', secondary: '#3385FF', tertiary: '#1A64E6', success: '#4CAF50', warning: '#FF9800', danger: '#F44336', background: '#1A1A1A', surface: '#262626', text: '#FFFFFF', textSecondary: '#B3B3B3' }
  },
  { 
    id: 'dark-compact', 
    title: 'Dark Compact Blue',   
    ionicKey: 'blue-dark',    
    devx: 'dark.compact',          
    dark: true,
    colors: { primary: '#4DA6FF', secondary: '#3385FF', tertiary: '#1A64E6', success: '#4CAF50', warning: '#FF9800', danger: '#F44336', background: '#1A1A1A', surface: '#262626', text: '#FFFFFF', textSecondary: '#B3B3B3' }
  },
  { 
    id: 'darkmoon',     
    title: 'Darkmoon',            
    ionicKey: 'blue-dark',    
    devx: 'darkmoon',              
    dark: true,
    colors: { primary: '#6B9BD1', secondary: '#5B8BC0', tertiary: '#4B7BAF', success: '#66BB6A', warning: '#FFA726', danger: '#EF5350', background: '#1A1A2E', surface: '#16213E', text: '#EAEAEA', textSecondary: '#A0A0A0' }
  },
  { 
    id: 'darkmoon-compact', 
    title: 'Darkmoon Compact', 
    ionicKey: 'blue-dark',    
    devx: 'darkmoon.compact',      
    dark: true,
    colors: { primary: '#6B9BD1', secondary: '#5B8BC0', tertiary: '#4B7BAF', success: '#66BB6A', warning: '#FFA726', danger: '#EF5350', background: '#1A1A2E', surface: '#16213E', text: '#EAEAEA', textSecondary: '#A0A0A0' }
  },
  { 
    id: 'darkviolet',   
    title: 'Dark Violet',         
    ionicKey: 'purple-dark',  
    devx: 'darkviolet',            
    dark: true,
    colors: { primary: '#B39DDB', secondary: '#9575CD', tertiary: '#7E57C2', success: '#66BB6A', warning: '#FFA726', danger: '#EF5350', background: '#1A1A1A', surface: '#2E2E3E', text: '#E0E0E0', textSecondary: '#A8A8A8' }
  },
  { 
    id: 'darkviolet-compact', 
    title: 'Dark Violet Compact', 
    ionicKey: 'purple-dark', 
    devx: 'darkviolet.compact', 
    dark: true,
    colors: { primary: '#B39DDB', secondary: '#9575CD', tertiary: '#7E57C2', success: '#66BB6A', warning: '#FFA726', danger: '#EF5350', background: '#1A1A1A', surface: '#2E2E3E', text: '#E0E0E0', textSecondary: '#A8A8A8' }
  },
];

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private renderer: Renderer2;
  private readonly LINK_ID = 'dx-theme-link';
  private readonly COMPACT_CLASS = 'dx-compact';
  private readonly LS_KEY = 'heralp_theme';
  private readonly USE_CACHE_BUSTER = !environment.production; // dev'de css cache kır

  // Tema değiştiğinde UI tarafına haber vermek istersen
  readonly currentPreset$ = new BehaviorSubject<ThemePreset | null>(null);
  readonly compact$ = new BehaviorSubject<boolean>(false);

  constructor(
    rendererFactory: RendererFactory2,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  /** Dışarıdan çağır: tek komutla Ionic + DevExtreme + Compact uygular ve kaydeder */
  applyPreset(presetId: string, compact = false): void {
    const preset = THEME_PRESETS.find(p => p.id === presetId) || THEME_PRESETS[0];

    // Ionic (renk paleti + dark)
    this.setIonicTheme(preset.ionicKey, preset.dark);

    // Renk şemasını uygula
    this.applyColorScheme(preset.colors);

    // DevExtreme (css switch + themes.current)
    this.applyDevExtremeTheme(preset.devx, compact);

    // Dashboard render'ı bittikten sonra theme'i tekrar apply et (chart'lar yüklensin)
    setTimeout(() => {
      this.fixDashboardTextColors(preset.colors);
    }, 800);

    // Persist
    localStorage.setItem(this.LS_KEY, JSON.stringify({ id: preset.id, compact }));

    this.currentPreset$.next(preset);
    this.compact$.next(!!compact);
  }

  /** Uygulama açılışında son seçimi geri yükle */
  restoreLastPreset(): void {
    const raw = localStorage.getItem(this.LS_KEY);
    const last = raw ? JSON.parse(raw) as { id: string; compact?: boolean } : null;
    const fallback = THEME_PRESETS[0];

    const preset = last
      ? (THEME_PRESETS.find(p => p.id === last.id) || fallback)
      : fallback;

    this.setIonicTheme(preset.ionicKey, preset.dark);
    this.applyColorScheme(preset.colors);
    this.applyDevExtremeTheme(preset.devx, !!last?.compact);

    this.currentPreset$.next(preset);
    this.compact$.next(!!last?.compact);
  }

  /** Sadece Ionic tarafını (CSS değişkenleri + dark) ayarla */
  setIonicTheme(themeKey: string, isDark: boolean): void {
    this.document.body.setAttribute('data-theme', themeKey);
    this.document.body.classList.toggle('dark', isDark);
  }

  /** DevExtreme tema css'ini güvenli şekilde değiştirir ve compact'ı ayarlar */
  applyDevExtremeTheme(theme: DevXTheme, compact = false): void {
    // Convert theme name to CSS file name
    // e.g., 'material.blue.light' -> 'dx.material.blue.light.css'
    //       'light.blue' -> 'dx.light.css'
    const cssFileName = `dx.${theme}.css`;
    const href = `assets/dx-themes/${cssFileName}`;

    let link = this.document.getElementById(this.LINK_ID) as HTMLLinkElement | null;

    const createLink = () => {
      const l = this.renderer.createElement('link') as HTMLLinkElement;
      l.id = this.LINK_ID;
      l.rel = 'stylesheet';
      l.type = 'text/css';
      this.renderer.appendChild(this.document.head, l);
      return l;
    };

    if (!link) link = createLink();

    // Aynı temayı tekrar yükleme
    if (link.href && link.href.includes(`dx.${theme}.css`)) {
      this.finishAfterCssLoaded(theme, compact);
      return;
    }

    // Olası eski listener'ları sıfırla
    link.onload = null;
    link.onerror = null;

    // Dev modda cache kırmak bazen gerekli
    const finalHref = this.USE_CACHE_BUSTER ? `${href}?v=${Date.now()}` : href;

    link.onload = () => this.finishAfterCssLoaded(theme, compact);
    link.onerror = () => console.warn('DevExtreme tema CSS yüklenemedi:', finalHref);

    link.href = finalHref;
  }

  /** CSS yüklendikten sonra yapılacaklar */
  private finishAfterCssLoaded(theme: DevXTheme, compact: boolean): void {
    // DevExtreme'e aktif temayı bildir
    // Null/undefined kontrolü - theme yoksa varsayılan kullan
    const safeTheme = theme || 'light';
    try {
      themes.current(safeTheme as any);
    } catch (e) {
      console.warn('DevExtreme theme set edilemedi:', e);
      // Fallback olarak light tema dene
      try {
        themes.current('light' as any);
      } catch (e2) {
        console.warn('Fallback theme de başarısız:', e2);
      }
    }

    // Compact sınıfı
    if (compact) {
      this.document.body.classList.add(this.COMPACT_CLASS);
    } else {
      this.document.body.classList.remove(this.COMPACT_CLASS);
    }

    // Bazı bileşenlerde layout tazelemek faydalı olur
    setTimeout(() => window.dispatchEvent(new Event('resize')), 0);
  }

  /** CSS variable'larını tema renklerine göre set et */
  applyColorScheme(colors: ThemeColorScheme): void {
    const root = this.document.documentElement;
    
    // App custom variables
    root.style.setProperty('--app-primary', colors.primary);
    root.style.setProperty('--app-secondary', colors.secondary);
    root.style.setProperty('--app-tertiary', colors.tertiary);
    root.style.setProperty('--app-success', colors.success);
    root.style.setProperty('--app-warning', colors.warning);
    root.style.setProperty('--app-danger', colors.danger);
    root.style.setProperty('--app-background', colors.background);
    root.style.setProperty('--app-surface', colors.surface);
    root.style.setProperty('--app-text', colors.text);
    root.style.setProperty('--app-text-secondary', colors.textSecondary);
    
    // Ionic color variables
    root.style.setProperty('--ion-color-primary', colors.primary);
    root.style.setProperty('--ion-color-primary-rgb', this.hexToRgb(colors.primary));
    root.style.setProperty('--ion-color-secondary', colors.secondary);
    root.style.setProperty('--ion-color-secondary-rgb', this.hexToRgb(colors.secondary));
    root.style.setProperty('--ion-color-tertiary', colors.tertiary);
    root.style.setProperty('--ion-color-tertiary-rgb', this.hexToRgb(colors.tertiary));
    root.style.setProperty('--ion-color-success', colors.success);
    root.style.setProperty('--ion-color-success-rgb', this.hexToRgb(colors.success));
    root.style.setProperty('--ion-color-warning', colors.warning);
    root.style.setProperty('--ion-color-warning-rgb', this.hexToRgb(colors.warning));
    root.style.setProperty('--ion-color-danger', colors.danger);
    root.style.setProperty('--ion-color-danger-rgb', this.hexToRgb(colors.danger));
    
    // Ionic background and text colors
    root.style.setProperty('--ion-background-color', colors.background);
    root.style.setProperty('--ion-text-color', colors.text);
    
    // Inject dynamic style to override Ionic's default palette colors
    this.injectIonicColorOverrides(colors);
    
    // Fix DevExtreme Dashboard text colors
    this.fixDashboardTextColors(colors);
  }

  /** DevExtreme Dashboard SVG text element'lerinin rengini düzelt - DARK MODE ONLY */
  private fixDashboardTextColors(colors: ThemeColorScheme): void {
    // Light tema'da DevExtreme defaults'ı kullan - dokunma
    // Dark tema'da text rengini beyaza çevir
    if (colors.text === '#FFFFFF') { // Dark tema
      this.applyDashboardTextColors(colors);
    }
  }

  /** Dashboard text rengini uygula - DARK TEMA ONLY */
  private applyDashboardTextColors(colors: ThemeColorScheme): void {
    // Daha uzun delay ile DOM hazır olmasını bekle
    const applyStyles = () => {
      const dashboardViewer = this.document.querySelector('.dx-dashboard-viewer') as HTMLElement;
      if (!dashboardViewer) {
        // Dashboard henüz yüklenmemişse, tekrar dene
        setTimeout(applyStyles, 300);
        return;
      }

      // Ana dashboard viewer'ın arka planını dark yap
      dashboardViewer.style.backgroundColor = colors.background;
      dashboardViewer.style.color = colors.text;

      // Dashboard container
      const dashboardContainer = dashboardViewer.querySelector('.dx-dashboard-container') as HTMLElement;
      if (dashboardContainer) {
        dashboardContainer.style.backgroundColor = colors.background;
        dashboardContainer.style.color = colors.text;
      }

      // Tüm dashboard item'larının arka planını dark surface rengine çevir
      const items = dashboardViewer.querySelectorAll('.dx-dashboard-item');
      items.forEach((item: Element) => {
        const htmlEl = item as HTMLElement;
        htmlEl.style.backgroundColor = colors.surface;
        htmlEl.style.color = colors.text;
        htmlEl.style.borderColor = colors.textSecondary;
      });

      // Dashboard item başlıklarını dark yap
      const itemTitles = dashboardViewer.querySelectorAll('.dx-dashboard-item-title');
      itemTitles.forEach((title: Element) => {
        const htmlEl = title as HTMLElement;
        htmlEl.style.backgroundColor = colors.surface;
        htmlEl.style.color = colors.text;
      });

      // Dashboard item content'i dark yap
      const itemContents = dashboardViewer.querySelectorAll('.dx-dashboard-item-content');
      itemContents.forEach((content: Element) => {
        const htmlEl = content as HTMLElement;
        htmlEl.style.backgroundColor = colors.surface;
        htmlEl.style.color = colors.text;
      });

      // SVG ve Chart element'lerinin text rengini beyaza çevir
      const textElements = dashboardViewer.querySelectorAll('text, tspan, svg text, svg tspan');
      textElements.forEach((el: Element) => {
        const htmlEl = el as HTMLElement;
        htmlEl.setAttribute('fill', colors.text);
        htmlEl.style.fill = colors.text;
        htmlEl.style.color = colors.text;
      });

      // BAR CHART FIX: Bar çubukları (rect elements) primary color'a çevir
      // Tüm SVG rect'leri tara ve transparent/siyah olanları primary color'a çevir
      const allSvgRects = dashboardViewer.querySelectorAll('svg rect');
      allSvgRects.forEach((rect: Element) => {
        const htmlEl = rect as SVGElement;
        const fill = htmlEl.getAttribute('fill') || '';
        const computedFill = window.getComputedStyle(htmlEl).fill;
        
        // Eğer fill yok, transparent, siyah, gri ise -> primary color yap
        if (!fill || fill === 'none' || fill === 'transparent' || 
            fill === '#000000' || fill === '#1f1f1f' || fill === '#333333' ||
            computedFill === 'rgb(0, 0, 0)' || computedFill.includes('0, 0, 0')) {
          
          // Kontrol: parent g'nin class'ı axis/grid değil mi?
          const parentG = htmlEl.parentElement;
          const isDataBar = !parentG?.classList.contains('dx-chart-axis') && 
                           !parentG?.classList.contains('dx-chart-grid') &&
                           !htmlEl.classList.contains('dx-chart-axis');
          
          if (isDataBar || !fill) {
            htmlEl.setAttribute('fill', colors.primary);
            htmlEl.style.fill = colors.primary;
            htmlEl.style.opacity = '1';
            htmlEl.setAttribute('opacity', '1');
          }
        }
      });

      // SVG path'ler için de (bazı chart'lar path kullanır)
      const allSvgPaths = dashboardViewer.querySelectorAll('svg path');
      allSvgPaths.forEach((path: Element) => {
        const htmlEl = path as SVGElement;
        const fill = htmlEl.getAttribute('fill') || '';
        
        // Siyah/gri fill'li path'leri primary color'a çevir (ama grid/axis çizgileri değil)
        if ((fill === '#000000' || fill === '#1f1f1f' || fill === '#333333') && !htmlEl.classList.contains('dx-chart-axis')) {
          htmlEl.setAttribute('fill', colors.primary);
          htmlEl.style.fill = colors.primary;
        }
      });

      // Canvas element'leri de oku (bazı chart'lar canvas kullanır)
      const canvases = dashboardViewer.querySelectorAll('canvas');
      canvases.forEach((canvas: Element) => {
        const htmlEl = canvas as HTMLElement;
        htmlEl.style.backgroundColor = colors.surface;
      });

      // Tüm dx-widget'ları kontrol et
      const widgets = dashboardViewer.querySelectorAll('.dx-widget, .dxc-chart, .dxda-table, .dxg-gauge');
      widgets.forEach((widget: Element) => {
        const htmlEl = widget as HTMLElement;
        htmlEl.style.backgroundColor = colors.surface;
        htmlEl.style.color = colors.text;
      });

      // REAL-TIME DOM MONITORING: MutationObserver ile yeni chart'ları takip et
      // (dinamik dashboard'larda yeni chart'lar eklenebilir)
      const observer = new MutationObserver(() => {
        // Tüm rect'leri kontrol et - fill attribute'ı olmayan veya transparent olanları primary color'a çevir
        const allRects = dashboardViewer.querySelectorAll('svg rect');
        allRects.forEach((rect: Element) => {
          const htmlEl = rect as SVGElement;
          const fill = htmlEl.getAttribute('fill') || '';
          const computedFill = window.getComputedStyle(htmlEl).fill;
          
          // Eğer rect fill'i yok, transparent, siyah veya gri ise -> primary color yap
          if (!fill || fill === 'none' || fill === 'transparent' || 
              fill === '#000000' || fill === '#1f1f1f' || fill === '#333333' ||
              computedFill === 'rgb(0, 0, 0)' || computedFill.includes('0, 0, 0')) {
            
            // Bar çubuk klasını kontrol et (parent g'nin class'ı)
            const parentG = htmlEl.parentElement;
            const isDataBar = !parentG?.classList.contains('dx-chart-axis') && 
                             !parentG?.classList.contains('dx-chart-grid') &&
                             !htmlEl.classList.contains('dx-chart-axis');
            
            if (isDataBar || !fill) {
              htmlEl.setAttribute('fill', colors.primary);
              htmlEl.style.fill = colors.primary;
              htmlEl.style.opacity = '1';
              htmlEl.setAttribute('opacity', '1');
            }
          }
        });

        // Yeni text element'leri de kontrol et
        const allTexts = dashboardViewer.querySelectorAll('svg text, svg tspan');
        allTexts.forEach((text: Element) => {
          const htmlEl = text as SVGElement;
          const fill = htmlEl.getAttribute('fill') || '';
          const computedFill = window.getComputedStyle(htmlEl).fill;
          
          // Siyah/gri text'leri beyaza çevir
          if (fill === '#000000' || computedFill === 'rgb(0, 0, 0)' || computedFill.includes('0, 0, 0')) {
            htmlEl.setAttribute('fill', colors.text);
            htmlEl.style.fill = colors.text;
          }
        });
      });

      // Observer config: subtree ve childList değişikliklerini izle
      observer.observe(dashboardViewer, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['fill', 'style', 'opacity']
      });
    };

    setTimeout(applyStyles, 500);
  }

  /** Ionic color palette'ını dynamically inject et */
  private injectIonicColorOverrides(colors: ThemeColorScheme): void {
    // Eski style tag'ı sil
    const existingStyle = this.document.getElementById('app-ionic-color-overrides');
    if (existingStyle) {
      existingStyle.remove();
    }
    
    // Yeni style tag oluştur - body selector ile all Ionic components'ı override et
    const style = this.document.createElement('style');
    style.id = 'app-ionic-color-overrides';
    style.textContent = `
      /* CRITICAL: Override ALL Ionic colors with maximum specificity */
      html, body, body * {
        --ion-color-primary: ${colors.primary} !important;
        --ion-color-primary-rgb: ${this.hexToRgb(colors.primary)} !important;
        --ion-color-primary-contrast: #ffffff !important;
        --ion-color-primary-contrast-rgb: 255, 255, 255 !important;
        --ion-color-primary-shade: ${this.shadeColor(colors.primary, -10)} !important;
        --ion-color-primary-tint: ${this.shadeColor(colors.primary, 10)} !important;

        --ion-color-secondary: ${colors.secondary} !important;
        --ion-color-secondary-rgb: ${this.hexToRgb(colors.secondary)} !important;
        --ion-color-secondary-contrast: #ffffff !important;
        --ion-color-secondary-contrast-rgb: 255, 255, 255 !important;
        --ion-color-secondary-shade: ${this.shadeColor(colors.secondary, -10)} !important;
        --ion-color-secondary-tint: ${this.shadeColor(colors.secondary, 10)} !important;

        --ion-color-tertiary: ${colors.tertiary} !important;
        --ion-color-tertiary-rgb: ${this.hexToRgb(colors.tertiary)} !important;
        --ion-color-tertiary-contrast: #ffffff !important;
        --ion-color-tertiary-contrast-rgb: 255, 255, 255 !important;
        --ion-color-tertiary-shade: ${this.shadeColor(colors.tertiary, -10)} !important;
        --ion-color-tertiary-tint: ${this.shadeColor(colors.tertiary, 10)} !important;

        --ion-color-success: ${colors.success} !important;
        --ion-color-success-rgb: ${this.hexToRgb(colors.success)} !important;
        --ion-color-success-contrast: #ffffff !important;
        --ion-color-success-contrast-rgb: 255, 255, 255 !important;
        --ion-color-success-shade: ${this.shadeColor(colors.success, -10)} !important;
        --ion-color-success-tint: ${this.shadeColor(colors.success, 10)} !important;

        --ion-color-warning: ${colors.warning} !important;
        --ion-color-warning-rgb: ${this.hexToRgb(colors.warning)} !important;
        --ion-color-warning-contrast: #000000 !important;
        --ion-color-warning-contrast-rgb: 0, 0, 0 !important;
        --ion-color-warning-shade: ${this.shadeColor(colors.warning, -10)} !important;
        --ion-color-warning-tint: ${this.shadeColor(colors.warning, 10)} !important;

        --ion-color-danger: ${colors.danger} !important;
        --ion-color-danger-rgb: ${this.hexToRgb(colors.danger)} !important;
        --ion-color-danger-contrast: #ffffff !important;
        --ion-color-danger-contrast-rgb: 255, 255, 255 !important;
        --ion-color-danger-shade: ${this.shadeColor(colors.danger, -10)} !important;
        --ion-color-danger-tint: ${this.shadeColor(colors.danger, 10)} !important;

        --ion-background-color: ${colors.background} !important;
        --ion-background-color-rgb: ${this.hexToRgb(colors.background)} !important;
        --ion-text-color: ${colors.text} !important;
        --ion-text-color-rgb: ${this.hexToRgb(colors.text)} !important;
        --ion-content-background: ${colors.surface} !important;
        
        --text-primary: ${colors.text} !important;
        --text-secondary: ${colors.textSecondary} !important;
        --background-color: ${colors.background} !important;
        --card-background: ${colors.surface} !important;
        --text-color: ${colors.text} !important;
        --border-color: ${colors.primary} !important;

        /* Color Steps */
        --ion-color-step-0: ${colors.background} !important;
        --ion-color-step-50: ${this.shadeColor(colors.surface, 5)} !important;
        --ion-color-step-100: ${colors.surface} !important;
        --ion-color-step-150: ${this.shadeColor(colors.surface, -5)} !important;
        --ion-color-step-200: ${this.shadeColor(colors.surface, -10)} !important;
        --ion-color-step-250: ${this.shadeColor(colors.surface, -15)} !important;
        --ion-color-step-300: ${this.shadeColor(colors.surface, -20)} !important;
        --ion-color-step-350: ${this.shadeColor(colors.surface, -25)} !important;
        --ion-color-step-400: ${this.shadeColor(colors.surface, -30)} !important;
        --ion-color-step-450: ${this.shadeColor(colors.surface, -35)} !important;
        --ion-color-step-500: ${this.shadeColor(colors.surface, -40)} !important;
        --ion-color-step-550: ${this.shadeColor(colors.surface, -45)} !important;
        --ion-color-step-600: ${this.shadeColor(colors.surface, -50)} !important;
        --ion-color-step-650: ${this.shadeColor(colors.surface, -55)} !important;
        --ion-color-step-700: ${this.shadeColor(colors.surface, -60)} !important;
        --ion-color-step-750: ${this.shadeColor(colors.surface, -65)} !important;
        --ion-color-step-800: ${this.shadeColor(colors.surface, -70)} !important;
        --ion-color-step-850: ${this.shadeColor(colors.surface, -75)} !important;
        --ion-color-step-900: ${this.shadeColor(colors.surface, -80)} !important;
        --ion-color-step-950: ${this.shadeColor(colors.surface, -85)} !important;
      }

      /* Ensure Menu and Toolbar colors */
      ion-menu, ion-header, ion-toolbar, ion-content {
        --ion-background-color: ${colors.background} !important;
        --ion-text-color: ${colors.text} !important;
        color: ${colors.text} !important;
      }

      ion-menu {
        --background: ${colors.surface} !important;
        --color: ${colors.text} !important;
        color: ${colors.text} !important;
      }

      ion-item {
        --background: ${colors.surface} !important;
        --color: ${colors.text} !important;
        color: ${colors.text} !important;
        --border-color: rgba(${this.hexToRgb(colors.text)}, 0.1) !important;
      }

      ion-label {
        color: ${colors.text} !important;
      }

      ion-text {
        color: ${colors.text} !important;
      }

      ion-title {
        color: ${colors.text} !important;
      }

      ion-button {
        --background: ${colors.primary} !important;
        --color: #ffffff !important;
        color: #ffffff !important;
      }

      ion-select {
        --placeholder-color: ${colors.textSecondary} !important;
        --placeholder-opacity: 1 !important;
        color: ${colors.text} !important;
      }

      ion-checkbox {
        --checkmark-color: ${colors.text} !important;
      }

      ion-radio {
        --color: ${colors.text} !important;
      }

      ion-toggle {
        --background: ${colors.surface} !important;
      }

      /* Tüm text element'lerini beyaza çevir dark modda */
      body.dark {
        color: ${colors.text} !important;
      }

      body.dark ion-content {
        color: ${colors.text} !important;
        --ion-text-color: ${colors.text} !important;
      }

      body.dark ion-item,
      body.dark ion-label,
      body.dark ion-text,
      body.dark ion-title,
      body.dark ion-card-title,
      body.dark ion-card-subtitle,
      body.dark .ion-text-wrap {
        color: ${colors.text} !important;
      }

      /* ===== DARK MODE ONLY: Dashboard SVG Text Styling ===== */
      ${colors.text === '#FFFFFF' ? `
      /* Dark theme - Dashboard container arka planları */
      .dx-dashboard-viewer {
        background-color: ${colors.background} !important;
      }
      
      .dx-dashboard-container {
        background-color: ${colors.background} !important;
      }
      
      .dx-dashboard-item {
        background-color: ${colors.surface} !important;
        border-color: ${colors.textSecondary} !important;
      }
      
      .dx-dashboard-item-content {
        background-color: ${colors.surface} !important;
      }
      
      /* Chart arka planları - DARK TEMA */
      .dx-dashboard-viewer .dxc-chart {
        background-color: ${colors.surface} !important;
      }
      
      .dx-dashboard-viewer svg {
        background-color: ${colors.surface} !important;
      }
      
      .dx-dashboard-viewer canvas {
        background-color: ${colors.surface} !important;
      }
      
      /* SVG rect'leri (chart arka planları) - DARK TEMA */
      .dx-dashboard-viewer svg rect {
        fill: ${colors.surface} !important;
      }
      
      /* Tüm non-background rect'leri (bar çubuklar) primary color'a çevir - DARK TEMA */
      .dx-dashboard-viewer svg g > rect {
        fill: ${colors.primary} !important;
        opacity: 1 !important;
      }
      
      .dx-dashboard-viewer svg g > g > rect {
        fill: ${colors.primary} !important;
        opacity: 1 !important;
      }
      
      /* Chart tipine göre bar styling - DARK TEMA */
      .dxc-chart svg g > rect {
        fill: ${colors.primary} !important;
        opacity: 1 !important;
      }
      
      /* fix SVG text visibility - DARK TEMA */
      .dx-dashboard-viewer text,
      .dx-dashboard-viewer tspan,
      .dx-dashboard-viewer svg text,
      .dx-dashboard-viewer svg tspan {
        fill: ${colors.text} !important;
      }

      /* Chart text labels - DARK TEMA */
      .dx-dashboard-viewer .dxc-series-label,
      .dx-dashboard-viewer .dxc-axis-label,
      .dx-dashboard-viewer .dxc-title {
        fill: ${colors.text} !important;
      }

      /* Dashboard item title text - DARK TEMA */
      .dx-dashboard-item-title {
        color: ${colors.text} !important;
      }

      /* Toolbar text - DARK TEMA */
      .dx-toolbar-item,
      .dx-toolbar-label {
        color: ${colors.text} !important;
      }

      /* Table text - DARK TEMA */
      .dx-row,
      .dx-cell {
        color: ${colors.text} !important;
      }
      ` : ''}
    `;
    
    this.document.head.appendChild(style);
  }

  /** Hex rengini RGB'ye çevir (virgülle ayrılmış) */
  private hexToRgb(hex: string): string {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (result) {
      const r = parseInt(result[1], 16);
      const g = parseInt(result[2], 16);
      const b = parseInt(result[3], 16);
      return `${r}, ${g}, ${b}`;
    }
    return '0, 0, 0';
  }

  /** Rengi biraz daha koyu/açık yapma */
  private shadeColor(hex: string, percent: number): string {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (result) {
      let r = parseInt(result[1], 16);
      let g = parseInt(result[2], 16);
      let b = parseInt(result[3], 16);
      
      r = Math.round(r + (255 - r) * (percent / 100));
      g = Math.round(g + (255 - g) * (percent / 100));
      b = Math.round(b + (255 - b) * (percent / 100));
      
      r = Math.max(0, Math.min(255, r));
      g = Math.max(0, Math.min(255, g));
      b = Math.max(0, Math.min(255, b));
      
      return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
    }
    return hex;
  }
}
